import { Inject } from '@odinjs/odin';
import { FormAPI, InputAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';

import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { BloodManagementTerminateAliases, FragmentNames } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../../controllers/BaseController';
import { YesNoType } from '../../../../../utils/enum-utils';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import MedicationService from '../../../services/medication-service';
import { GenerateChangePrescriptionRequest } from '../blood-management-types';

@Controller({ component: BloodManagementTerminateAliases.BLOOD_MANAGEMENT_TERMINATE_LAYOUT, fragment: FragmentNames.BLOOD_MANAGEMENT_TERMINATE })
export default class BloodManagementTerminateController extends BaseController {

  @Inject(BloodManagementTerminateAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(BloodManagementTerminateAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(BloodManagementTerminateAliases.BLOOD_MANAGEMENT_TERMINATE_FORM)
  private formAPI!: FormAPI;

  @Inject(BloodManagementTerminateAliases.NOTES_TEXT_AREA)
  private justificationTextAreaAPI!: InputAPI;

  @Inject(BloodManagementTerminateAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @OnMount(BloodManagementTerminateAliases.BLOOD_MANAGEMENT_TERMINATE_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
  }

  @OnMount(BloodManagementTerminateAliases.DATE_INPUT)
  handleDateFieldMount(event: MountEvent): void {
    const api = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    api.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnBeforeCancel(BloodManagementTerminateAliases.BLOOD_MANAGEMENT_TERMINATE_FORM)
  onFormCancel(): void {
    applicationContext.popNavigationItem();
  }

  @OnChange(BloodManagementTerminateAliases.DISCARDED_VOLUME_INPUT)
  handleInfusedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  @OnChange(BloodManagementTerminateAliases.INFUSED_VOLUME_INPUT)
  handleDiscardedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  private isValidChar(value: string, oldValue?: string): boolean {
    return value === '.' || value === ',' || (value === '' && oldValue !== '')
  }

  private handleVolumeInputChange({ value, oldValue }: ChangeEvent<string>, input: InputAPI): void {
    const actualValue = Number(value) || 0;
    if (this.isValidChar(value, oldValue) || isNaN(actualValue) || oldValue === value) {
      return
    }

    const { stepVolume } = this.historyService.getCurrentRouteParams();
    const partialVolume = Number(this.partialVolumeInputAPI.getValue());
    const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, Number(stepVolume));
    input.setValue(String(updatedVolume));
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const defaultValue = 0;
    if (stageVolume > defaultValue) {
      const result = stageVolume - partialVolume - infusedVolume;
      return Math.max(defaultValue, parseFloat(result.toFixed(2)));
    }

    return defaultValue;
  }

  @OnMount(BloodManagementTerminateAliases.BLOOD_MANAGEMENT_TERMINATE_FORM)
  handleMountForm({ target }: EventData<FormAPI>): void {
    const { partialVolume, stepVolume } = this.historyService.getCurrentRouteParams() as { partialVolume: number, stepVolume: number };
    const discardedVolume = 0
    const infusedVolume = stepVolume - partialVolume - discardedVolume
    target.setData({
      discardedVolume,
      infusedVolume,
      partialVolume,
      stageVolume: stepVolume,
    })
  }

  @OnBeforeSubmit(BloodManagementTerminateAliases.BLOOD_MANAGEMENT_TERMINATE_FORM)
  handleBeforeSubmit(): void {
    const body = this.createRequestBody() as Record<string, Value>
    this.handleTerminateEnteralNutritionRequest(body);
  }

  private createRequestBody(): GenerateChangePrescriptionRequest {
    const { encounterId, prescriptionNumber, startDate, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const { currentTime } = this.context.getBulkData() as { currentTime: string };
    const terminateSolutionType = 3;
    const changeType = 4;
    const formValues = this.formAPI.getValue()
    const body = {
      changeDate: currentTime,
      changeType,
      dateHour: moment(startDate as string).format(ISO_FULL_DATE_FORMAT),
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue() as number ?? 0,
      encounterNumber: Number(encounterId),
      infusedVolumeQuantity: formValues.infusedVolume as number ?? 0,
      infusionSpeedQuantity: Number(formValues.infusionSpeed) ?? 0,
      isPalm: YesNoType.YES,
      minutesPauseQuantity: 0,
      observationDescription: this.justificationTextAreaAPI.getValue() as string,
      partialVolumeQuantity: formValues.partialVolume as number,
      prescriptionNumber: Number(prescriptionNumber),
      solutionSequenceNumber: Number(sequenceNumber),
      solutionType: terminateSolutionType,
      typeDosage: '',
      volumeSafeQuantity: formValues.infusedVolume as number,
    } as GenerateChangePrescriptionRequest

    return body;
  }

  private handleTerminateEnteralNutritionRequest(body: Record<string, Value>): Promise<void> {
    this.formAPI.setLoading(true)
    this.formAPI.setEditing(false)
    return HttpService.post(
      Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR,
      {},
      { body }
    ).then(response => {
      const responseBody = response.body as {
        messageDescription: string;
      };
      if (responseBody) {
        this.showResponseMessage(responseBody?.messageDescription);
        this.redirectToTimeline();
      }
    }).catch(error => {
      if (error.body && error.body.errors) {
        error.body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
      }
    }).finally(() => {
      this.formAPI.setEditing(true)
      this.formAPI.setLoading(false)
    });
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('label.infusion-viewer.toast-infusion-terminated'), ToastLevel.SUCCESS);
  }
}